import { type ProjectSchemaJSON, RuntimeSchema } from '@takeshape/schema';
import type { Maybe } from '@takeshape/util';
import { getUserId } from 'app/auth/selectors';
import type { ProjectSchemaCompat } from 'app/compat/types';
import { getCurrentProjectId } from 'app/project/selectors';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { createSelector } from 'reselect';
import { compatSchemaToSchema } from '../compat/utils.js';
import { DEFAULT_BRANCH, NAME, SCRATCH_BRANCH } from './constants.js';
import type { Branch, BranchesSlice, BranchesState, ServiceLayerStateMap } from './types.js';
import { branchToBranchQueryArgs, createBranchPath, toServiceLayers } from './utils.js';

export const getState = (state: BranchesSlice): BranchesState => state[NAME];
export const getBranchArg = (_: unknown, args: { branchName?: string }) => args?.branchName;

export const getBranchByName = createSelector(
  [getState, (_: unknown, name: string) => name],
  (state: BranchesState, name: string): Branch | undefined => state.branches[name]
);

export const getCurrentBranchName = createSelector(getState, (state) => state.currentBranch);

export const getBranchName = createSelector(
  getCurrentBranchName,
  getBranchArg,
  (currentBranchName, branchName) => branchName ?? currentBranchName
);

export const getCurrentBranchPath = createSelector(
  [getCurrentProjectId, getCurrentBranchName],
  (projectId, branchName) => createBranchPath(projectId, branchName)
);

export const getDefaultBranchPath = createSelector([getCurrentProjectId], (projectId) => createBranchPath(projectId));

export const getUseScratchForPreview = createSelector(getState, (state) => state.useScratchForPreview);

export const getPreviewBranchName = createSelector(
  [getState, getUseScratchForPreview],
  (state, useScratchForPreview) => (useScratchForPreview ? SCRATCH_BRANCH : state.currentBranch)
);

export const getCurrentBranch = createSelector(getState, (state) => state.branches[state.currentBranch]);

export const getPreviewBranch = createSelector(
  [getState, getPreviewBranchName],
  (state, previewBranch) => state.branches[previewBranch]
);

export const getPreviewBranchLayers = createSelector([getPreviewBranch], (state) => state.layers);
export const getPreviewBranchRuntimeSchema = createSelector([getPreviewBranch], (state) => state.runtimeSchema.data);

export const getPreviewBranchRuntimeSchemaModel = createSelector(
  [getPreviewBranchRuntimeSchema, getPreviewBranchLayers],
  (runtimeSchemaJson, layers) =>
    runtimeSchemaJson ? RuntimeSchema.fromJson(runtimeSchemaJson, toServiceLayers(layers)) : null
);

export const getPreviewBranchServiceLayers = createSelector(
  [getPreviewBranchLayers],
  (state): ServiceLayerStateMap => omit(state, ['user'])
);

export const getScratchUserSchema = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> => get(state, ['branches', SCRATCH_BRANCH, 'layers', 'user', 'data']) ?? undefined
);

export const getScratchRuntimeSchema = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> => get(state, ['branches', SCRATCH_BRANCH, 'runtimeSchema', 'data']) ?? undefined
);

export const getSchemaHash = createSelector(
  getState,
  (state): Maybe<string> => get(state, ['branches', SCRATCH_BRANCH, 'runtimeSchema', 'data', 'schemaHash']) ?? undefined
);

export const getScratchVersion = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> =>
    get(state, ['branches', SCRATCH_BRANCH, 'layers', 'user', 'data', 'version']) ?? undefined
);

export const getLayerSchema = createSelector(
  [getState, (_: unknown, layerName: string) => layerName],
  (state, layerName): Maybe<ProjectSchemaCompat> =>
    get(state, ['branches', state.currentBranch, 'layers', layerName, 'data']) ?? undefined
);

export const getScratchLayerSchema = createSelector(
  [getState, (_: unknown, layerName: string) => layerName],
  (state, layerName): Maybe<ProjectSchemaCompat> =>
    get(state, ['branches', SCRATCH_BRANCH, 'layers', layerName, 'data']) ?? undefined
);

export const getUserSchema = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> =>
    get(state, ['branches', state.currentBranch, 'layers', 'user', 'data']) ?? undefined
);

export const getUserSchemaNotCompat = createSelector(
  getUserSchema,
  (schema): Maybe<ProjectSchemaJSON> => (schema ? compatSchemaToSchema(schema) : undefined)
);

export const getRuntimeSchema = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> =>
    get(state, ['branches', state.currentBranch, 'runtimeSchema', 'data']) ?? undefined
);

export const getDefaultUserSchema = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> => get(state, ['branches', DEFAULT_BRANCH, 'layers', 'user', 'data']) ?? undefined
);

export const getDefaultRuntimeSchema = createSelector(
  getState,
  (state): Maybe<ProjectSchemaCompat> => get(state, ['branches', DEFAULT_BRANCH, 'runtimeSchema', 'data']) ?? undefined
);

export const getBranchQueryArgs = createSelector(
  [getCurrentProjectId, getBranchName, getUserId],
  (projectId, currentBranch, userId) => {
    return branchToBranchQueryArgs(projectId, currentBranch, userId);
  }
);

export const getCurrentBranchQueryArgs = createSelector(
  [getCurrentProjectId, getCurrentBranchName, getUserId],
  (projectId, currentBranch, userId) => {
    return branchToBranchQueryArgs(projectId, currentBranch, userId);
  }
);

export const getApiVersion = createSelector(getRuntimeSchema, (schema) => schema?.apiVersion ?? '0');

export const getBranchMergeState = createSelector(getState, (state) => state.branchMerge);

export const getCurrentBranchNeedsUpdate = createSelector(getState, (state) => state.currentBranchNeedsUpdate);

export const getCurrentBranchCanPromote = createSelector(getState, (state) => state.currentBranchCanPromote);
